import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ShieldCheck, Mail, RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import logoCapta from "../assets/logo-capta.png";

function validarSenha(senha) {
  return [
    { label: "Mínimo de 8 caracteres", ok: senha.length >= 8 },
    { label: "1 letra maiúscula (A-Z)", ok: /[A-Z]/.test(senha) },
    { label: "1 letra minúscula (a-z)", ok: /[a-z]/.test(senha) },
    { label: "1 número (0-9)", ok: /[0-9]/.test(senha) },
    { label: "1 caractere especial (!@#...)", ok: /[^A-Za-z0-9]/.test(senha) },
  ];
}

function safeDecode(value) {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

async function safeReadJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export default function ResetSenha() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  // useSearchParams geralmente já vem decodificado; só tenta decode se tiver %
  const email = useMemo(() => {
    const raw = (params.get("email") || "").trim();
    return raw.includes("%") ? safeDecode(raw) : raw;
  }, [params]);

  const [senha, setSenha] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [fatalMsg, setFatalMsg] = useState("");

  const regras = useMemo(() => validarSenha(senha), [senha]);
  const senhaForte = regras.every((r) => r.ok);
  const confere =
    senha.length > 0 && confirmar.length > 0 && senha === confirmar;

  const podeEnviar = !!token && !!email && senhaForte && confere && !carregando;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFatalMsg("");

    if (!token || !email) {
      toast.error("Link inválido. Solicite um novo reset.");
      return;
    }
    if (!senhaForte) {
      toast.error("Senha fraca. Ajuste os requisitos.");
      return;
    }
    if (!confere) {
      toast.error("As senhas não conferem.");
      return;
    }

    try {
      setCarregando(true);

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const endpoint = `${API_BASE_URL}/auth/reset`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, novaSenha: senha }),
      });

      const data = await safeReadJson(res);

      if (!res.ok) {
        const msg =
          data?.erro ||
          data?.message ||
          "Não foi possível redefinir. Solicite um novo link.";

        const msgLower = String(msg).toLowerCase();
        if (
          msgLower.includes("token inválido") ||
          msgLower.includes("token expirado") ||
          msgLower.includes("já utilizado") ||
          msgLower.includes("solicite novamente")
        ) {
          setFatalMsg(msg);
          return;
        }

        toast.error(msg);
        return;
      }

      toast.success("✅ Senha redefinida com sucesso! Faça login.");
      navigate("/login");
    } catch (err) {
      toast.error("Erro ao conectar com o servidor.");
    } finally {
      setCarregando(false);
    }
  };

  // Link faltando parâmetros
  if (!token || !email) {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-900 to-blue-600 flex items-center justify-center py-6 px-4">
        <div className="bg-white rounded-3xl shadow-2xl px-6 pt-2 pb-6 max-w-md w-full">
          <div className="flex justify-center">
            <img
              src={logoCapta}
              alt="Logo CaptaCRM"
              className="w-40 drop-shadow-2xl"
            />
          </div>

          <div className="bg-yellow-100 border border-yellow-300 text-yellow-900 px-4 py-3 rounded-xl text-sm font-medium shadow-sm mt-2">
            Link inválido ou incompleto. Solicite uma nova redefinição.
          </div>

          <button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full py-3 rounded-xl bg-blue-700 text-white font-semibold shadow-md hover:bg-blue-800 transition-all mt-4"
          >
            Voltar ao login
          </button>

          <p className="text-center text-sm text-gray-500 mt-4">
            © {new Date().getFullYear()} CaptaCRM. AWC Consultoria Sistemas
            Ltda.
          </p>
        </div>
      </div>
    );
  }

  // Token inválido/expirado/usado
  if (fatalMsg) {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-900 to-blue-600 flex items-center justify-center py-6 px-4">
        <div className="bg-white rounded-3xl shadow-2xl px-6 pt-2 pb-6 max-w-md w-full">
          <div className="flex justify-center">
            <img
              src={logoCapta}
              alt="Logo CaptaCRM"
              className="w-40 drop-shadow-2xl"
            />
          </div>

          <div className="text-center mt-2">
            <h1 className="text-xl font-extrabold text-blue-900 flex items-center justify-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-700" />
              Link inválido
            </h1>
            <p className="text-sm text-gray-600 mt-2 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="font-semibold break-all">{email}</span>
            </p>
          </div>

          <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-xl text-sm font-medium shadow-sm mt-4">
            {fatalMsg}
          </div>

          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full py-3 rounded-xl bg-blue-700 text-white font-semibold shadow-md hover:bg-blue-800 transition-all"
            >
              Voltar ao login
            </button>

            <button
              type="button"
              onClick={() => {
                toast.info("Peça um novo link em “Esqueci minha senha”.");
                navigate("/login");
              }}
              className="w-full py-2 rounded-xl bg-gray-100 text-blue-900 font-semibold shadow-sm hover:bg-gray-200 transition flex items-center justify-center gap-2"
            >
              <RefreshCcw className="w-4 h-4" />
              Solicitar novo link
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4">
            © {new Date().getFullYear()} CaptaCRM. AWC Consultoria Sistemas
            Ltda.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-900 to-blue-600 flex items-center justify-center py-6 px-4">
      <div className="bg-white rounded-3xl shadow-2xl px-6 pt-0 pb-6 max-w-md w-full">
        <div className="flex justify-center mb-0">
          <img
            src={logoCapta}
            alt="Logo CaptaCRM"
            className="w-40 drop-shadow-2xl transition-transform duration-700 ease-out animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          />
        </div>

        <div className="text-center mt-0">
          <h1 className="text-2xl font-extrabold text-blue-900 flex items-center justify-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-700" />
            Redefinir senha
          </h1>
          <p className="text-sm text-gray-600 mt-2 flex items-center justify-center gap-2">
            <Mail className="w-4 h-4" />
            <span className="font-semibold break-all">{email}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-5">
          <div className="flex flex-col relative">
            <label className="text-sm font-medium text-blue-800 mb-1 flex items-center gap-2">
              <Lock className="w-5 h-5" /> Nova senha
            </label>
            <input
              type={showSenha ? "text" : "password"}
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="p-3 border border-blue-300 rounded-xl shadow-sm bg-white text-blue-900 outline-blue-500 pr-10"
              required
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowSenha(!showSenha)}
              className="absolute right-3 top-9 text-blue-600 hover:text-blue-800"
              aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {showSenha ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>

          <div className="flex flex-col relative">
            <label className="text-sm font-medium text-blue-800 mb-1 flex items-center gap-2">
              <Lock className="w-5 h-5" /> Confirmar senha
            </label>
            <input
              type={showConfirmar ? "text" : "password"}
              autoComplete="new-password"
              value={confirmar}
              onChange={(e) => setConfirmar(e.target.value)}
              className="p-3 border border-blue-300 rounded-xl shadow-sm bg-white text-blue-900 outline-blue-500 pr-10"
              required
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowConfirmar(!showConfirmar)}
              className="absolute right-3 top-9 text-blue-600 hover:text-blue-800"
              aria-label={showConfirmar ? "Ocultar senha" : "Mostrar senha"}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              {showConfirmar ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
            <p className="font-semibold text-blue-900 mb-2">Requisitos</p>
            <ul className="space-y-1 text-xs text-gray-700">
              {regras.map((r) => (
                <li key={r.label} className="flex items-center gap-2">
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${r.ok ? "bg-green-600" : "bg-gray-300"}`}
                  />
                  {r.label}
                </li>
              ))}
            </ul>

            {confirmar.length > 0 && (
              <p
                className={`mt-2 text-xs font-semibold ${confere ? "text-green-700" : "text-red-600"}`}
              >
                {confere ? "✅ Senhas conferem" : "❌ Senhas não conferem"}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!podeEnviar}
            className="w-full py-3 rounded-xl bg-blue-700 text-white font-semibold shadow-md hover:shadow-xl hover:bg-blue-800 transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:bg-blue-700"
          >
            {carregando ? "Salvando..." : "Redefinir senha"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full py-2 rounded-xl bg-gray-100 text-blue-900 font-semibold shadow-sm hover:bg-gray-200 transition"
          >
            Voltar ao login
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          © {new Date().getFullYear()} CaptaCRM. AWC Consultoria Sistemas Ltda.
        </p>
      </div>
    </div>
  );
}
